// src/services/rank-utils.ts
import { indexedDBStorage } from './storage/indexedDB';

// デフォルトのランクの表示色
export const defaultRankColors: Record<string, string> = {
  S: '#f5222d',  // 赤
  A: '#fa8c16',  // オレンジ
  B: '#52c41a',  // 緑
  C: '#1890ff',  // 青
  D: '#722ed1',  // 紫
  E: '#bfbfbf',  // グレー
  '受注前': '#f5222d',  // 赤
  '検討中': '#fa8c16',  // オレンジ
  '案件化': '#52c41a',  // 緑
  '失注': '#bfbfbf',    // グレー
  '受注済': '#1890ff',  // 青
};

// IndexedDBに保存するキー
export const RANK_COLORS_KEY = 'rank_colors';

// 同期バージョンのランク色取得関数（直接使用できる）
export const getRankColorSync = (rankName: string, customRankColors?: Record<string, string>): string => {
  // カスタムランク色が指定されている場合はそれを使用
  const rankColors = customRankColors || defaultRankColors;
  
  // 指定されたランク名の色があれば返す
  if (rankName && rankColors[rankName]) {
    return rankColors[rankName];
  }
  
  // デフォルト色を返す
  return '#bfbfbf';
};

// 顧客リストからランク色マッピングを動的に生成する関数
export const generateRankColors = (customers: any[]): Record<string, string> => {
  // 現在のランク色を複製
  const newRankColors = { ...defaultRankColors };
  
  // 顧客リストからユニークなランク情報を抽出
  const uniqueRanks = Array.from(new Set(
    customers
      .map(customer => customer.rankname)
      .filter(rankname => rankname) // undefined/nullを除外
  ));
  
  // ランク情報と一緒にranknoも取得して順序を考慮
  const ranksWithOrder = uniqueRanks.map(rankName => {
    // 同じランク名を持つ顧客の中から最初のranknoを取得
    const customer = customers.find(c => c.rankname === rankName);
    const rankno = customer?.rankno !== undefined ? customer.rankno : -1;
    return { rankName, rankno };
  });
  
  // ranknoの値でソート（数値が小さいほど上位のランク）
  ranksWithOrder.sort((a, b) => {
    if (a.rankno === -1) return 1;  // 不明なranknoは後ろに
    if (b.rankno === -1) return -1; // 不明なranknoは後ろに
    return a.rankno - b.rankno;     // 昇順でソート
  });
  
  // ソートされた順序に基づいて色を割り当て
  ranksWithOrder.forEach((rank, index) => {
    if (rank.rankName) {
      // rankColorPalette（順序ベースの色パレット）から色を取得
      newRankColors[rank.rankName] = getRankColorByIndex(index);
      
    }
  });
  
  // 生成したランク色マッピングをIndexedDBに保存
  try {
    // 同期的に実行されるため、awaitはつけない
    indexedDBStorage.setItem(RANK_COLORS_KEY, JSON.stringify(newRankColors))
      .then(() => {})
      .catch(error => {
        console.error('ランク色の保存エラー:', error);
      });
  } catch (error) {
    console.error('ランク色の保存エラー:', error);
  }
  
  return newRankColors;
};

// 標準ランク色パレット - 順序ベースの色選択用（getRankColorByIndex 専用）
const rankColorPalette = [
  '#f5222d',  // 赤 - 最上位ランク（1番目）
  '#fa8c16',  // オレンジ - 2番目
  '#52c41a',  // 緑 - 3番目
  '#1890ff',  // 青 - 4番目
  '#722ed1',  // 紫 - 5番目
  '#bfbfbf',  // グレー - 6番目以降
];

// ランクの順序（インデックス）に基づいて色を取得する関数
export const getRankColorByIndex = (rankIndex: number): string => {
  // 有効なインデックスかチェック
  if (rankIndex === undefined || rankIndex === null || rankIndex < 0) {
    return '#bfbfbf'; // デフォルト色
  }
  
  // ランクの数値に基づいて決められた色のパターンに変換
  // ここで重要なのは、同じrankno値に対して常に同じ色を返すこと
  
  // 色を選択（パレットの範囲を超える場合は最後の色を使用）
  const colorIndex = rankIndex % rankColorPalette.length;  // 循環させる
  return rankColorPalette[colorIndex];
};

/**
 * ランクマスタ上の「rankno 昇順の順位(index)」に基づいて色を返す。
 *
 * getRankColorByIndex(rankno) は rankno（マスタID）をそのままパレットの添字として
 * 扱うため、rankno が 0,1,2… の連番でないと色がズレる（画面間でも食い違う）。
 * こちらはランクマスタ全体を rankno 昇順に並べた順位で色を決めるので、
 * generateRankColors（rank_colors マッピング）と同じ割り当てになり、
 * ランクマスタさえ同じなら地図・詳細・一覧のどこでも同じランクが同じ色になる。
 *
 * @param rankno 対象のランクNo
 * @param rankMaster ランクマスタ（rankno を持つ要素の配列。順不同でよい）
 * @returns 色コード。該当が無ければ既定色（グレー）
 */
export const getRankColorByMasterOrder = (
  rankno: number | null | undefined,
  rankMaster: Array<{ rankno: number }>,
): string => {
  if (rankno == null || rankno < 0 || !rankMaster || rankMaster.length === 0) {
    return '#bfbfbf';
  }
  const sorted = rankMaster
    .filter((r) => r != null && typeof r.rankno === 'number' && r.rankno >= 0)
    .sort((a, b) => a.rankno - b.rankno);
  const index = sorted.findIndex((r) => r.rankno === rankno);
  if (index < 0) return '#bfbfbf';
  return getRankColorByIndex(index);
};

// ランク名と色の対応を全体のランク色マッピング（rank_colors）へ反映する関数
export const saveCustomerRank = async (
  customerId: number,
  rankName: string,
  rankIndex: number = -1,
  rankColor?: string
): Promise<void> => {
  try {
    // 色が明示的に指定されていない場合はインデックスから取得
    const color = rankColor || (rankIndex >= 0 ? getRankColorByIndex(rankIndex) : '');

    // defaultRankColors を直接書き換えないよう複製してから使う
    let storedRankColors: Record<string, string> = { ...defaultRankColors };
    const storedRankColorsStr = await indexedDBStorage.getItem(RANK_COLORS_KEY);
    if (storedRankColorsStr) {
      storedRankColors = JSON.parse(storedRankColorsStr);
    }

    // ランク名と色のマッピングを更新
    if (rankName && color) {
      storedRankColors[rankName] = color;
      await indexedDBStorage.setItem(RANK_COLORS_KEY, JSON.stringify(storedRankColors));
    }
  } catch (error) {
    console.error(`顧客ID ${customerId} のランク色マッピング更新エラー:`, error);
  }
};